// Presentation Layer - Hook para PWA
import { useState, useEffect } from 'react';
import { Platform } from 'react-native';
import { PWAService } from '../../application/services/PWAService';
import { WebPWAServiceWorkerAdapter } from '../../infrastructure/pwa/PWAServiceWorkerAdapter';
import { WebPWAInstallAdapter } from '../../infrastructure/pwa/PWAInstallAdapter';

export const usePWA = () => {
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [pwaService] = useState(() => {
    if (Platform.OS !== 'web') return null;
    return new PWAService(
      new WebPWAServiceWorkerAdapter(),
      new WebPWAInstallAdapter()
    );
  });

  useEffect(() => {
    if (Platform.OS !== 'web' || !pwaService) return;

    // Inicializar PWA
    pwaService.initialize();

    // Verificar estado inicial
    setIsInstalled(pwaService.isInstalled());
    setCanInstall(pwaService.canInstall());

    // Escuchar eventos
    const handleInstallable = () => {
      setCanInstall(true);
    };

    const handleInstalled = () => {
      setCanInstall(false);
      setIsInstalled(true);
    };

    const handleUpdateAvailable = () => {
      setUpdateAvailable(true);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('pwa-installable', handleInstallable);
      window.addEventListener('pwa-installed', handleInstalled);
      window.addEventListener('pwa-update-available', handleUpdateAvailable);

      return () => {
        window.removeEventListener('pwa-installable', handleInstallable);
        window.removeEventListener('pwa-installed', handleInstalled);
        window.removeEventListener('pwa-update-available', handleUpdateAvailable);
      };
    }
  }, [pwaService]);

  const install = async () => {
    if (!pwaService) return false;
    const success = await pwaService.install();
    if (success) {
      setCanInstall(false);
      setIsInstalled(true);
    }
    return success;
  };

  const applyUpdate = () => {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration?.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          window.location.reload();
        }
      });
    }
  };

  return {
    canInstall,
    isInstalled,
    updateAvailable,
    install,
    applyUpdate,
    isPWASupported: Platform.OS === 'web',
  };
};
